import {
  createStore,
  Store as VuexStore,
  CommitOptions,
  DispatchOptions,
  useStore as baseUseStore,
} from 'vuex'
import { actions } from './actions'
import { mutations } from './mutations'
import { getters } from './getters'
import { State } from '@/store/types'

// Example modules
import authModule from './modules/auth'
import catalogModule from './modules/catalog'
import bookingModule from './modules/booking'
import documentModule from './modules/document'
import modalModule from './modules/modal'
import pageMetaModule from './modules/pagemeta'

const state: State = {}

const store: VuexStore<State> = createStore<State>({
  state,
  mutations,
  actions,
  getters,
  modules: {
    catalog: catalogModule,
    auth: authModule,
    booking: bookingModule,
    document: documentModule,
    modal: modalModule,
    pagemeta: pageMetaModule,
  },
})

// Utility types to extract parameters and return types
type ExtractActionParameters<T> = T extends (...args: infer P) => unknown
  ? P
  : never
type ExtractActionReturnType<T> = T extends (...args: unknown[]) => infer R
  ? R
  : never

export default store

export type Store = Omit<VuexStore<State>, 'commit' | 'dispatch'> & {
  commit<K extends keyof typeof mutations>(
    key: K,
    payload: Parameters<(typeof mutations)[K]>[1],
    options?: CommitOptions
  ): ReturnType<(typeof mutations)[K]>
} & {
  dispatch<K extends keyof typeof actions>(
    key: K,
    payload?: ExtractActionParameters<(typeof actions)[K]>[1],
    options?: DispatchOptions
  ): ExtractActionReturnType<(typeof actions)[K]>
}

export const useStore = () => {
  return baseUseStore() as Store
}
